import React, { useState, useEffect } from 'react';
import { 
  CheckCircle2, 
  Clock, 
  ChevronLeft, 
  Copy, 
  QrCode, 
  ShieldCheck, 
  Sparkles,
  ArrowRight,
  Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardFooter, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { getOrderDetails, Order } from '@/db/api';
import QRCodeDataUrl from '@/components/ui/qrcodedataurl';
import { toast } from 'sonner';

const OrderDetailsPage: React.FC = () => {
  const navigate = useNavigate();
  const { orderNo } = useParams<{ orderNo: string }>();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchOrder = async () => {
    if (!orderNo) return;
    try {
      const data = await getOrderDetails(orderNo);
      setOrder(data);
      if (data.status === 'paid') {
        toast.success('支付成功！您的会员权益已激活');
      }
    } catch (err) {
      console.error('Fetch order error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrder();
    // Poll for status if pending
    let interval: any;
    if (order?.status === 'pending') {
      interval = setInterval(fetchOrder, 3000);
    }
    return () => clearInterval(interval);
  }, [orderNo, order?.status]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <Loader2 className="h-10 w-10 animate-spin text-primary" />
    </div>
  );

  if (!order) return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6 text-center">
      <Card className="max-w-md w-full p-8 rounded-2xl shadow-xl">
        <h2 className="text-2xl font-bold mb-4 text-red-500">订单不存在</h2>
        <Button onClick={() => navigate('/membership')}>返回会员中心</Button>
      </Card>
    </div>
  );

  const isPaid = order.status === 'paid';

  return (
    <div className="min-h-screen bg-background pb-20 animate-fade-in">
      <div className="tech-flow-bg h-[200px] flex items-center px-6">
        <div className="container mx-auto">
          <Button 
            variant="ghost" 
            className="text-white hover:bg-white/10 mb-2 gap-2"
            onClick={() => navigate('/membership')}
          >
            <ChevronLeft className="h-4 w-4" />
            返回
          </Button>
          <h1 className="text-3xl font-bold text-white flex items-center gap-2">
            订单详情
          </h1>
        </div>
      </div>

      <div className="container mx-auto px-4 -mt-10">
        <div className="max-w-2xl mx-auto">
          <Card className="rounded-2xl shadow-xl overflow-hidden border-border/50">
            <CardHeader className={`p-8 text-center ${isPaid ? 'bg-success/5' : 'bg-primary/5'}`}>
              <div className="flex justify-center mb-4">
                {isPaid ? (
                  <div className="w-20 h-20 rounded-full bg-success/10 flex items-center justify-center text-success">
                    <CheckCircle2 className="h-10 w-10" />
                  </div>
                ) : (
                  <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                    <Clock className="h-10 w-10 animate-pulse" />
                  </div>
                )}
              </div>
              <CardTitle className="text-2xl font-bold">
                {isPaid ? '支付成功' : '订单待支付'}
              </CardTitle>
              <CardDescription className="mt-2 font-medium">
                订单编号: {order.order_no}
              </CardDescription>
            </CardHeader>

            <CardContent className="p-8 space-y-8">
              {!isPaid && (
                <div className="flex flex-col items-center space-y-6">
                  <div className="p-4 bg-white rounded-2xl shadow-inner border-2 border-primary/10">
                    <QRCodeDataUrl 
                      text={order.wechat_pay_url} 
                      width={200} 
                      className="rounded-lg"
                    />
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-foreground">应付金额：¥{order.total_amount}</p>
                    <p className="text-sm text-muted-foreground mt-2">请使用微信扫码完成支付</p>
                  </div>
                  <div className="flex items-center gap-2 text-primary text-xs font-bold uppercase tracking-wider bg-primary/5 px-4 py-2 rounded-full border border-primary/10">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    正在同步支付状态...
                  </div>
                </div>
              )}

              <div className="space-y-4 pt-4">
                <div className="flex justify-between items-center py-3 border-b border-border/50">
                  <span className="text-muted-foreground font-medium">商品名称</span>
                  <span className="font-bold">{order.sku_code === 'membership_paid' ? '正式会员 (月度)' : '内测会员 (永久)'}</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-border/50">
                  <span className="text-muted-foreground font-medium">下单时间</span>
                  <span className="font-medium">{new Date(order.created_at).toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-border/50">
                  <span className="text-muted-foreground font-medium">支付方式</span>
                  <span className="font-medium flex items-center gap-1">
                    微信支付
                  </span>
                </div>
                {isPaid && (
                  <div className="flex justify-between items-center py-3 border-b border-border/50">
                    <span className="text-muted-foreground font-medium">生效状态</span>
                    <Badge className="bg-success text-white">已实时生效</Badge>
                  </div>
                )}
              </div>
            </CardContent>

            <CardFooter className="p-8 pt-0 flex flex-col gap-4">
              {isPaid ? (
                <Button 
                  className="w-full h-12 bg-primary text-white hover:bg-primary/90 shadow-lg font-bold text-lg rounded-xl gap-2"
                  onClick={() => navigate('/ai-hub')}
                >
                  进入 AI 助手
                  <ArrowRight className="h-5 w-5" />
                </Button>
              ) : (
                <div className="w-full flex gap-3">
                  <Button variant="outline" className="flex-1 h-12 border-border font-bold rounded-xl" onClick={() => navigate('/membership')}>
                    暂不支付
                  </Button>
                  <Button className="flex-1 h-12 bg-primary text-white font-bold rounded-xl" onClick={() => fetchOrder()}>
                    我已支付
                  </Button>
                </div>
              )}
              
              <div className="flex items-center justify-center gap-2 text-muted-foreground text-xs mt-4">
                <ShieldCheck className="h-4 w-4" />
                <span>万象聚聚光安全支付保障</span>
              </div>
            </CardFooter>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default OrderDetailsPage;
